<template>
  <BaseSelect
    :options="formattedOptions"
    :model-value="modelValue"
    :placeholder="placeholder"
    :searchable="true"
    :clearable="clearable"
    :with-button="withButton"
    :button-label="buttonLabel"
    :no-results-text="noResultsText"
    :external-search="true"
    @update:model-value="handleUpdate"
    @change="handleChange"
    @button-click="$emit('button-click')"
    @search="handleSearch"
    @clear-search="handleClearSearch"
    @dropdown-scroll-end="handleScrollEnd"
  />
</template>

<script>
import { useWaybillStore } from "@/stores/waybillStore";
import { getCompanyId } from "@/utils/userUtils";
import waybillsApi from "@/services/api/waybillsApi";

export default {
  name: "WaybillSelect",

  props: {
    modelValue: {
      type: [String, Number],
      default: null,
    },
    placeholder: {
      type: String,
      default: "Выберите путевой лист",
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    noResultsText: {
      type: String,
      default: "Путевой лист не найден",
    },
    withButton: {
      type: Boolean,
      default: false,
    },
    buttonLabel: {
      type: String,
      default: "Создать путевой лист",
    },
  },

  emits: ["update:modelValue", "change", "button-click"],

  data() {
    return {
      companyId: getCompanyId(),
      preselectedOption: null,
      isSearchActive: false,
    };
  },

  computed: {
    waybillStore() {
      return useWaybillStore();
    },

    formattedOptions() {
      const options = this.waybillStore.waybills.map((w) => ({
        value: w.id,
        label: w.selector_name,
      }));

      if (
        this.preselectedOption &&
        !this.isSearchActive &&
        !options.find(
          (o) => String(o.value) === String(this.preselectedOption.value)
        )
      ) {
        return [this.preselectedOption, ...options];
      }

      return options;
    },
  },

  watch: {
    async modelValue(newVal, oldVal) {
      if (!newVal) {
        this.preselectedOption = null;
        return;
      }
      if (newVal === oldVal) return;
      if (!this.isSearchActive && !this.preselectedOption) {
        await this.fetchPreselectedOption();
      }
    },
  },

  methods: {
    handleUpdate(value) {
      if (!value) this.preselectedOption = null;
      this.$emit("update:modelValue", value);
    },

    handleChange(option) {
      this.preselectedOption = option
        ? { value: option.value, label: option.label }
        : null;
      this.$emit("change", option ? option.value : null);
    },

    async handleSearch(searchData) {
      this.isSearchActive = true;
      await this.waybillStore.fetchWaybills({ search: searchData.query });
    },

    async handleClearSearch() {
      this.isSearchActive = false;
      await this.waybillStore.fetchWaybills();
    },

    async handleScrollEnd() {
      if (
        this.waybillStore.pagination.page < this.waybillStore.pagination.pages &&
        !this.waybillStore.dataIsLoading
      ) {
        await this.waybillStore.fetchWaybills({
          page: this.waybillStore.pagination.page + 1,
        });
      }
    },

    async fetchPreselectedOption() {
      if (!this.modelValue) return;

      const exists = this.waybillStore.waybills.find(
        (w) => String(w.id) === String(this.modelValue)
      );
      if (exists) {
        this.preselectedOption = {
          value: exists.id,
          label: exists.selector_name,
        };
        return;
      }

      try {
        const response = await waybillsApi.show(this.companyId, this.modelValue);
        const w = response.data.result;
        this.preselectedOption = {
          value: w.id,
          label: w.selector_name,
        };
      } catch (error) {
        console.error("Ошибка при загрузке путевого листа:", error);
      }
    },
  },

  async mounted() {
    await this.waybillStore.fetchWaybills();
    await this.fetchPreselectedOption();
  },
};
</script>
